import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { productsAPI } from '@/lib/api';
import { formatInt } from '@/lib/format';

// Static presentation for each product type — rules copy is authored here rather
// than pulled from the database so the marketing copy stays predictable.
const TYPE_META = {
  one_step: {
    name: 'One-Step Program',
    tagline: 'Pay $20 once. Pass a single-phase evaluation. Get funded.',
    tag: '1-PHASE EVALUATION',
    leverage: '1:100',
    daily_dd: '4%',
    max_dd: '8%',
    min_days: 3,
    profit_target: '15%',
    payout_days: 14,
    consistency_pct: null,
  },
  instant: {
    name: 'Instant Funding',
    tagline: 'Skip the evaluation. Start directly on a simulated performance account.',
    tag: 'DIRECT PERFORMANCE',
    leverage: '1:100',
    daily_dd: '3%',
    max_dd: '6%',
    min_days: 3,
    profit_target: null,
    payout_days: 21,
    consistency_pct: '20%',
  },
};

export default function Rules() {
  const [products, setProducts] = useState([]);
  useEffect(() => { productsAPI.list().then(setProducts).catch(() => setProducts([])); }, []);

  const byType = (() => {
    const g = {};
    for (const p of products) (g[p.product_type] ||= []).push(p);
    for (const t of Object.keys(g)) g[t].sort((a, b) => a.account_size - b.account_size);
    return g;
  })();
  const typesOnPage = ['one_step', 'instant'].filter(t => (byType[t] || []).length > 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="label-sm mb-3">Trading rules</div>
        <h1 className="font-display text-4xl sm:text-5xl">Transparent by design.</h1>
        <p className="text-zinc-400 max-w-2xl mt-4">
          We keep our rules simple and equity-based. No hidden restrictions, no silent rule changes —
          just a measurable edge and clear risk boundaries within a structured evaluation.
        </p>

        {/* Core evaluation rules */}
        <section className="mt-14">
          <h2 className="font-display text-2xl text-[#00ff66] mb-4">The rules that matter</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RuleCard num="1" title="Daily Drawdown" body="Calculated based on equity at 00:00 UTC. Breach if your equity at any time of the day falls below the calculated limit." />
            <RuleCard num="2" title="Max Drawdown" body="Overall loss allowed on the account, measured as a percentage of your initial balance." />
            <RuleCard num="3" title="Minimum Trading Days" body="At least 3 separate days to complete the evaluation." />
            <RuleCard num="4" title="Weekends" body="Weekend holding is allowed, unless otherwise notified." />
            <RuleCard num="5" title="Stop Loss" body="Trading without an active stop loss is highly prohibited." />
            <RuleCard num="6" title="Maximum Open Positions" body="You may hold a maximum of five (5) open positions at any given time. Holding more than five simultaneous positions is a hard breach of your account." />
            <RuleCard num="7" title="Breach Handling" body="Termination of the simulated account. You may purchase a new evaluation program to restart." />
          </div>
        </section>

        {/* Per-plan rules */}
        <section className="mt-16">
          <h2 className="font-display text-2xl text-[#00ff66] mb-4">Rules per program</h2>
          {typesOnPage.length === 0 && (
            <div className="card-tech rounded-lg p-8 text-center text-sm text-zinc-500" data-testid="rules-empty">
              Program pricing is being staged. Check back shortly.
            </div>
          )}
          <div className="space-y-6">
            {typesOnPage.map(t => {
              const meta = TYPE_META[t];
              const sizes = byType[t];
              return (
                <div key={t} className="card-tech rounded-lg p-6" data-testid={`rules-${t}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="label-sm">{meta.tag}</div>
                      <h3 className="font-display text-2xl mt-1">{meta.name}</h3>
                      <p className="text-sm text-zinc-500 mt-1">{meta.tagline}</p>
                    </div>
                    <div className="text-right">
                      <div className="label-xs">ACCOUNT SIZES</div>
                      <div className="font-mono mt-1 text-sm">{sizes.map(s => `$${formatInt(s.account_size)}`).join(' · ')}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <Kv k="Leverage" v={meta.leverage} />
                    <Kv k="Daily drawdown" v={meta.daily_dd} />
                    <Kv k="Max drawdown" v={meta.max_dd} />
                    <Kv k="Minimum trading days" v={meta.min_days} />
                    {meta.profit_target && <Kv k="Profit objective" v={meta.profit_target} />}
                    {meta.consistency_pct && <Kv k="Profit consistency" v={`${meta.consistency_pct} rule`} />}
                    <Kv k={t === 'instant' ? 'First payout eligible' : 'First payout eligible'}
                        v={`${meta.payout_days} days after first trade`} />
                    <Kv k="Platform" v="MetaTrader 5" />
                  </div>
                  {t === 'instant' && (
                    <div className="mt-5 rounded-lg border border-[#00ff66]/30 bg-[#00ff66]/[0.04] p-4" data-testid="instant-consistency-callout">
                      <div className="label-xs text-[#00ff66]">INSTANT-ONLY RULE</div>
                      <div className="font-display text-lg mt-1">20% Profit Consistency Rule</div>
                      <p className="text-sm text-zinc-400 mt-2">
                        Your single best trading day must not exceed <span className="text-white">20% of your total profit</span> at the time of a reward request.
                        This is checked at payout review only — it never breaches your account.
                        If your best day is over the limit, simply continue trading consistently across more days to bring it back into range, then re-request your reward.
                      </p>
                      <p className="text-xs text-zinc-500 mt-3">
                        Example: if your total simulated profit is $1,000, your single best day must be ≤ $200. A $300 day means you'd need ~$1,500 total profit before the request qualifies.
                      </p>
                    </div>
                  )}
                  {t === 'one_step' && (
                    <div className="mt-5 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="one-step-unlock-callout">
                      <div className="label-xs">TWO-PAYMENT MODEL</div>
                      <div className="font-display text-lg mt-1">$20 today · unlock fee after passing</div>
                      <p className="text-sm text-zinc-400 mt-2">
                        The One-Step Program is <span className="text-white">$20 flat</span> to start, regardless of account size.
                        Once you hit the <span className="text-white">15% objective</span> within our rules, you pay a per-size unlock fee to activate your funded account.
                        No admin approval is required before the unlock fee is paid — the flow is automatic.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function RuleCard({ num, title, body }) {
  return (
    <div className="card-tech rounded-lg p-5">
      <div className="label-xs text-[#00ff66]">RULE {num}</div>
      <h3 className="font-display text-xl mt-1">{title}</h3>
      <p className="text-sm text-zinc-400 mt-2">{body}</p>
    </div>
  );
}
function Kv({ k, v }) {
  return (<div><div className="label-xs">{k}</div><div className="font-mono text-lg mt-1">{v}</div></div>);
}
